import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Radio, Input, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getInventaireConfig, getInventaires, createInventaire } from '../../services/inventaireService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard,
  NdoeStatusBadge, NdoeError,
} from '../../components/ndoe';
import useRefLists from '../rapports/useRefLists';
import CataloguePicker from '../journalTransactions/CataloguePicker';
import './inventaires.css';

const STATUTS = {
  en_cours: { tone: 'info', label: 'En cours' },
  valide: { tone: 'ok', label: 'Validé' },
  abandonne: { tone: 'neutral', label: 'Abandonné' },
};
const statutBadge = (s) => {
  const d = STATUTS[s] || { tone: 'neutral', label: s };
  return <NdoeStatusBadge kind="custom" tone={d.tone} label={d.label} />;
};
const typeBadge = (t) => (
  <NdoeStatusBadge kind="custom" tone={t === 'total' ? 'gold' : 'info'} label={t === 'total' ? 'Total' : 'Cyclique'} />
);

const EMPTY_FORM = { type: 'cyclique', q: '', id_marque: '', id_categorie: '', commentaire: '' };

export default function Inventaires() {
  const navigate = useNavigate();

  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [produits, setProduits] = useState([]);   // périmètre « articles précis » choisis en galerie

  const refs = useRefLists(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data }] = await Promise.all([getInventaireConfig(), getInventaires()]);
      setCfg(c);
      setRows(data.inventaires || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les inventaires.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const peutCompter = !!cfg?.peut_compter;

  const submitCreate = async () => {
    setCreating(true);
    try {
      const perimetre = form.type === 'total' ? {} : {
        q: form.q || undefined,
        id_marque: form.id_marque || undefined,
        id_categorie: form.id_categorie || undefined,
        id_produits: produits.length ? produits.map((x) => x.id_produit) : undefined,
      };
      const { data } = await createInventaire({ type: form.type, perimetre, commentaire: form.commentaire || undefined });
      toast.success(`${data.reference} créé : ${data.lignes} article(s) à compter.`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setProduits([]);
      navigate(`/inventaires/${data.id_inventaire}`);
    } catch (e) {
      toast.error(e.response?.data?.error || "Création de l'inventaire impossible.");
    } finally { setCreating(false); }
  };

  const avancement = (r) => {
    const total = Number(r.lignes_total) || 0;
    const fait = Number(r.lignes_comptees) || 0;
    const pctv = total ? Math.round((fait / total) * 100) : 0;
    return (
      <div className="inv-avancement">
        <span className="num">{fait}/{total}</span>
        <div className="inv-barre" aria-hidden="true"><i style={{ width: `${pctv}%` }} /></div>
      </div>
    );
  };
  const ecarts = (r) => (
    <span className="inv-ecarts num">
      {Number(r.paires_manquantes) > 0 && <b className="is-moins">−{r.paires_manquantes}</b>}
      {Number(r.paires_surplus) > 0 && <b className="is-plus">+{r.paires_surplus}</b>}
      {!Number(r.paires_manquantes) && !Number(r.paires_surplus) && '—'}
    </span>
  );

  const columns = [
    { title: 'Référence', dataIndex: 'reference', width: 110, render: (v) => <b>{v}</b> },
    { title: 'Type', dataIndex: 'type', width: 104, render: (t) => typeBadge(t) },
    { title: 'Statut', dataIndex: 'statut', width: 110, render: (s) => statutBadge(s) },
    { title: 'Avancement', dataIndex: 'lignes_total', width: 170, render: (_, r) => avancement(r) },
    { title: 'Écarts (paires)', dataIndex: 'paires_manquantes', align: 'right', width: 130, render: (_, r) => ecarts(r) },
    {
      title: 'À recompter', dataIndex: 'a_recompter', align: 'right', width: 110,
      render: (v) => (Number(v) > 0 ? <b className="inv-warn num">{v}</b> : <span className="num">—</span>),
    },
    { title: 'Commentaire', dataIndex: 'commentaire', render: (v) => v || '—' },
    { title: 'Créé le', dataIndex: 'date_cr', width: 150, render: (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—') },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_inventaire}
      title={`${r.reference} · ${r.type === 'total' ? 'Total' : 'Cyclique'}`}
      subtitle={r.commentaire || (r.date_cr ? new Date(r.date_cr).toLocaleDateString('fr-FR') : '')}
      badge={statutBadge(r.statut)}
      metrics={[
        { label: 'Comptées', value: `${r.lignes_comptees}/${r.lignes_total}` },
        { label: 'Manquants', value: Number(r.paires_manquantes) || 0 },
        { label: 'Surplus', value: Number(r.paires_surplus) || 0 },
        { label: 'À recompter', value: Number(r.a_recompter) || 0 },
      ]}
      onClick={() => navigate(`/inventaires/${r.id_inventaire}`)}
    />
  );

  if (cfg && !peutCompter) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Inventaires est réservé aux rôles autorisés (paramètre « inventaire_roles »)." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Stock"
        title="Inventaires"
        subtitle="Compter le rayon — tout le catalogue ou quelques articles — et justifier chaque écart."
        actions={
          <NdoeActionButton icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Nouvel inventaire
          </NdoeActionButton>
        }
      />

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          data={rows}
          columns={columns}
          renderCard={renderCard}
          rowKey="id_inventaire"
          loading={loading}
          onRetry={load}
          scroll={{ x: 1000 }}
          onRow={(r) => ({ onClick: () => navigate(`/inventaires/${r.id_inventaire}`), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 15 }}
          empty={{
            icon: '📋',
            title: 'Aucun inventaire',
            text: 'Créez votre premier inventaire — total, ou cyclique sur quelques articles.',
          }}
        />
      )}

      <Modal open={createOpen} onCancel={() => setCreateOpen(false)} footer={null} width={560}
        title="Nouvel inventaire" centered>
        <div className="inv-form">
          <Radio.Group
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            options={[
              { value: 'cyclique', label: 'Cyclique — quelques articles' },
              { value: 'total', label: 'Total — tout le catalogue' },
            ]}
          />
          {form.type === 'cyclique' && (
            <>
              <p className="inv-aide">
                Ciblez les articles à compter : une recherche par nom, une marque ou une catégorie
                (les critères se cumulent).
              </p>
              <Input placeholder="Recherche produit (ex. escarpin vernis)" value={form.q} maxLength={80}
                onChange={(e) => setForm((f) => ({ ...f, q: e.target.value }))} allowClear />
              <div className="inv-form__chips">
                <NdoeActionButton size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>
                  🖼 Choisir des articles dans la galerie
                </NdoeActionButton>
                {produits.map((x) => (
                  <span className="inv-chip" key={x.id_produit}>
                    {x.nom}
                    <button type="button" aria-label="Retirer" onClick={() => setProduits((l) => l.filter((y) => y.id_produit !== x.id_produit))}>×</button>
                  </span>
                ))}
              </div>
              <div className="inv-form__selects">
                <Select placeholder="Marque" options={refs.marques} value={form.id_marque || undefined}
                  onChange={(v) => setForm((f) => ({ ...f, id_marque: v ?? '' }))} allowClear showSearch optionFilterProp="label" />
                <Select placeholder="Catégorie" options={refs.categories} value={form.id_categorie || undefined}
                  onChange={(v) => setForm((f) => ({ ...f, id_categorie: v ?? '' }))} allowClear showSearch optionFilterProp="label" />
              </div>
            </>
          )}
          {form.type === 'total' && (
            <p className="inv-aide">
              Toutes les variantes actives du catalogue seront à compter. La session reste ouverte
              tant que tout n'est pas validé — comptez à votre rythme, la boutique continue de vendre.
            </p>
          )}
          <Input.TextArea rows={2} maxLength={255} placeholder="Commentaire (ex. inventaire de fin de saison)"
            value={form.commentaire} onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))} />
          <div className="inv-form__actions">
            <NdoeActionButton variant="secondary" onClick={() => setCreateOpen(false)}>Annuler</NdoeActionButton>
            <NdoeActionButton loading={creating} onClick={submitCreate}>Créer l'inventaire</NdoeActionButton>
          </div>
        </div>
      </Modal>
      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} refLists={refs}
        onSelect={(v) => {
          setProduits((l) => (l.some((x) => x.id_produit === v.id_produit) ? l
            : [...l, { id_produit: v.id_produit, nom: v.nom_produit }]));
          toast.success(`${v.nom_produit} ajouté au périmètre.`);
        }} />
    </NdoePage>
  );
}
